import { useEffect, useState, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/Button";
import { Text } from "@/components/Typography";
import { nylasApi } from "@/services/api";

type CallbackStatus = "loading" | "success" | "error";

/**
 * Decode the return path from the OAuth state parameter (base64url JSON: {uid, rp}).
 * This lets us redirect the user back to where they started even on error,
 * without depending on the backend response.
 */
function decodeReturnPath(state: string | null): string {
  if (!state) return "/profile";
  try {
    const padding = (4 - (state.length % 4)) % 4;
    const padded = state + "=".repeat(padding);
    const data = JSON.parse(atob(padded.replace(/-/g, "+").replace(/_/g, "/")));
    const rp = data.rp;
    if (typeof rp === "string" && rp.startsWith("/") && !rp.includes("//")) return rp;
  } catch {
    // Ignore decode errors — fall back to /profile
  }
  return "/profile";
}

/** True when this page is loaded inside a popup opened by window.open(). */
function isInPopup(): boolean {
  try {
    return !!window.opener && window.opener !== window;
  } catch {
    return false;
  }
}

export function OAuthCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<CallbackStatus>("loading");
  const [errorMessage, setErrorMessage] = useState("");

  const state = searchParams.get("state");
  const returnPath = useMemo(() => decodeReturnPath(state), [state]);
  const inPopup = useMemo(() => isInPopup(), []);

  useEffect(() => {
    const code = searchParams.get("code");

    if (!code || !state) {
      setStatus("error");
      setErrorMessage("Missing authorization code or state. Please try connecting again.");
      return;
    }

    let cancelled = false;

    async function exchangeCode() {
      try {
        const result = await nylasApi.handleCallback(code!, state!);
        if (!cancelled) {
          setStatus("success");

          // Broadcast to any same-origin tab listening (e.g. the tab that
          // opened this one). BroadcastChannel is reliable even after
          // cross-origin OAuth redirects that nullify window.opener.
          const channel = new BroadcastChannel("nylas_oauth");
          channel.postMessage({
            type: "NYLAS_OAUTH_SUCCESS",
            grantId: result.grantId,
          });
          channel.close();

          // Close this tab if it was opened by the app; otherwise redirect.
          if (inPopup) {
            window.close();
          } else {
            navigate(result.returnPath || returnPath, { replace: true });
          }
        }
      } catch (err: unknown) {
        if (!cancelled) {
          setStatus("error");

          // Extract meaningful error message — handle axios errors and plain errors
          let message = "Failed to connect calendar. Please try again.";
          if (err && typeof err === "object") {
            const axiosErr = err as {
              response?: { status?: number; data?: { detail?: string } };
              message?: string;
            };
            if (axiosErr.response?.status === 502) {
              message =
                "Your calendar provider returned an error. This can happen with certain account types. " +
                "You can try again later or continue without connecting your calendar.";
            } else if (axiosErr.response?.data?.detail) {
              message = axiosErr.response.data.detail;
            } else if (axiosErr.message) {
              message = axiosErr.message;
            }
          } else if (err instanceof Error) {
            message = err.message;
          }
          setErrorMessage(message);

          const channel = new BroadcastChannel("nylas_oauth");
          channel.postMessage({ type: "NYLAS_OAUTH_ERROR", message });
          channel.close();

          if (inPopup) {
            window.close();
          }
        }
      }
    }

    exchangeCode();
    return () => {
      cancelled = true;
    };
  }, [searchParams, navigate, state, returnPath, inPopup]);

  return (
    <div
      className="flex min-h-screen items-center justify-center"
      style={{ backgroundColor: "var(--color-secondary-50)" }}
    >
      <div
        className="w-full max-w-md rounded-xl border p-8 text-center shadow-sm"
        style={{
          backgroundColor: "var(--color-background-base)",
          borderColor: "var(--color-border-subtle)",
        }}
      >
        {status === "loading" && (
          <>
            <Loader2
              className="mx-auto h-10 w-10 animate-spin"
              style={{ color: "var(--color-primary-500)" }}
            />
            <Text
              as="p"
              weight="bold"
              className="mt-4"
              style={{ color: "var(--color-text-primary)" }}
            >
              Connecting your calendar...
            </Text>
            <Text as="p" size="2" className="mt-2" style={{ color: "var(--color-text-secondary)" }}>
              Please wait while we complete the calendar connection.
            </Text>
          </>
        )}

        {status === "success" && (
          <>
            <div
              className="mx-auto flex h-12 w-12 items-center justify-center rounded-full"
              style={{ backgroundColor: "var(--color-status-success-bg)" }}
            >
              <CheckCircle2
                className="h-6 w-6"
                style={{ color: "var(--color-status-success-text)" }}
              />
            </div>
            <Text
              as="p"
              weight="bold"
              className="mt-4"
              style={{ color: "var(--color-text-primary)" }}
            >
              Calendar Connected!
            </Text>
            <Text as="p" size="2" className="mt-2" style={{ color: "var(--color-text-secondary)" }}>
              {inPopup ? "You can close this tab." : "Redirecting you back..."}
            </Text>
          </>
        )}

        {status === "error" && (
          <>
            <div
              className="mx-auto flex h-12 w-12 items-center justify-center rounded-full"
              style={{ backgroundColor: "var(--color-accent-150)" }}
            >
              <XCircle className="h-6 w-6" style={{ color: "var(--color-action-primary)" }} />
            </div>
            <Text
              as="p"
              weight="bold"
              className="mt-4"
              style={{ color: "var(--color-text-primary)" }}
            >
              Connection Failed
            </Text>
            <Text as="p" size="2" className="mt-2" style={{ color: "var(--color-text-secondary)" }}>
              {errorMessage}
            </Text>
            {!inPopup && (
              <div className="mt-6 flex flex-col gap-3">
                <Button
                  variant="primaryGradient"
                  size="sm"
                  onClick={() => navigate(returnPath, { replace: true })}
                >
                  Try Again
                </Button>
                <button
                  className="text-sm font-medium transition-colors"
                  style={{ color: "var(--color-text-secondary)" }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = "var(--color-text-primary)")}
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.color = "var(--color-text-secondary)")
                  }
                  onClick={() =>
                    navigate(returnPath, { replace: true, state: { calendarError: true } })
                  }
                >
                  Continue without calendar
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default OAuthCallbackPage;
